<script lang="ts" setup>
import { db } from "@dopplexr/editor/lib/connectors/dexie";
import {
	IconBrightness,
	IconDeviceDesktop,
	IconLogout,
	IconMoon,
	IconRestore,
	IconSettings,
	IconSun,
	IconUsers,
} from "@tabler/icons-vue";
import { useColorMode } from "@vueuse/core";
import { rm } from "@zenfs/core/promises";
import {
	DropdownMenuRoot as DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuRadioGroup,
	DropdownMenuSub,
} from "reka-ui";
import { computed } from "vue";
import { RouterLink, useRouter } from "vue-router";
import Avatar from "@/components/ui/avatar/Avatar.vue";
import AvatarFallback from "@/components/ui/avatar/AvatarFallback.vue";
import Button from "@/components/ui/button/Button.vue";
import DropdownMenuContent from "@/components/ui/dropdown-menu/DropdownMenuContent.vue";
import DropdownMenuItem from "@/components/ui/dropdown-menu/DropdownMenuItem.vue";
import DropdownMenuLabel from "@/components/ui/dropdown-menu/DropdownMenuLabel.vue";
import DropdownMenuRadioItem from "@/components/ui/dropdown-menu/DropdownMenuRadioItem.vue";
import DropdownMenuSeparator from "@/components/ui/dropdown-menu/DropdownMenuSeparator.vue";
import DropdownMenuSubContent from "@/components/ui/dropdown-menu/DropdownMenuSubContent.vue";
import DropdownMenuSubTrigger from "@/components/ui/dropdown-menu/DropdownMenuSubTrigger.vue";
import DropdownMenuTrigger from "@/components/ui/dropdown-menu/DropdownMenuTrigger.vue";
import { useAuthStore } from "@/stores/authStore";
import IconWorkspaces from "../../assets/icons/workspaces.svg";

const authStore = useAuthStore();
const { store } = useColorMode();
const colorMode = computed({
	get() {
		return store.value === "auto" ? "auto" : store.value;
	},
	set(value) {
		if (value === "auto") {
			store.value = "auto";
		} else {
			store.value = value;
		}
	},
});
const colorModes = [
	{
		title: "Light",
		icon: IconSun,
		value: "light",
	},
	{
		title: "Dark",
		icon: IconMoon,
		value: "dark",
	},
	{
		title: "System",
		icon: IconDeviceDesktop,
		value: "auto",
	},
];

const router = useRouter();

async function resetCache() {
	db.delete();
	await rm("/", { force: true, recursive: true }).catch((err) => {
		console.warn(err);
	});
	location.reload();
}

async function logout() {
	// TODO: Implement Gitea logout
	localStorage.clear();
	db.delete();
	const root = await navigator.storage.getDirectory();
	for await (const name of root.keys()) {
		await root.removeEntry(name, { recursive: true });
	}
	authStore.clearAuthUser();
	await router.push({ name: "Login" });
}

const authUser = computed(() => authStore.user);
const userName = computed(() => authUser.value?.name);
const email = computed(() => authUser.value?.email);

const avatarInitials = computed(() =>
	authUser.value?.name
		?.trim()
		.split(/\s+/)
		.map((w: string) => w[0].toUpperCase())
		.join("")
		.slice(0, 2),
);
</script>

<template>
	<DropdownMenu>
		<DropdownMenuTrigger as-child>
			<Button variant="ghost" class="relative h-8 w-8 rounded-full">
				<Avatar class="h-8 w-8">
					<AvatarFallback>{{ avatarInitials }}</AvatarFallback>
				</Avatar>
			</Button>
		</DropdownMenuTrigger>
		<DropdownMenuContent class="w-56" align="end">
			<DropdownMenuLabel class="font-normal flex">
				<div class="flex flex-col space-y-1">
					<p class="text-sm font-medium leading-none">
						{{ userName || "User Info" }}
					</p>
					<p v-if="email" class="text-xs leading-none text-muted-foreground">
						{{ email }}
					</p>
				</div>
			</DropdownMenuLabel>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem as-child>
					<RouterLink to="/settings">
						<IconSettings class="w-5 h-5 mr-2 text-muted-foreground" />
						Settings
					</RouterLink>
				</DropdownMenuItem>
				<DropdownMenuItem as-child>
					<RouterLink to="/workspaces">
						<IconWorkspaces class="w-5 h-5 mr-2 text-muted-foreground" />
						Workspaces
					</RouterLink>
				</DropdownMenuItem>
				<DropdownMenuItem as-child>
					<RouterLink to="/team">
						<IconUsers class="w-5 h-5 mr-2 text-muted-foreground" />
						Team
					</RouterLink>
				</DropdownMenuItem>
				<DropdownMenuSub>
					<DropdownMenuSubTrigger>
						<IconBrightness class="w-5 h-5 mr-2 text-muted-foreground" />
						Theme
					</DropdownMenuSubTrigger>
					<DropdownMenuPortal>
						<DropdownMenuSubContent>
							<DropdownMenuRadioGroup v-model="colorMode">
								<DropdownMenuRadioItem
									v-for="mode in colorModes"
									:key="mode.value"
									:value="mode.value"
								>
									<component
										:is="mode.icon"
										class="mr-2 h-4 w-4 text-muted-foreground"
									/>
									{{ mode.title }}
								</DropdownMenuRadioItem>
							</DropdownMenuRadioGroup>
						</DropdownMenuSubContent>
					</DropdownMenuPortal>
				</DropdownMenuSub>
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuItem @click.prevent="resetCache">
				<IconRestore class="w-5 h-5 mr-2 text-muted-foreground" />
				Reset Cache
			</DropdownMenuItem>
			<DropdownMenuItem @click.prevent="logout">
				<IconLogout class="w-5 h-5 mr-2 text-muted-foreground" />
				Log out
			</DropdownMenuItem>
		</DropdownMenuContent>
	</DropdownMenu>
</template>
